<script>
import { GlTooltip, GlLoadingIcon, GlAlert, GlBadge, GlIcon } from '@gitlab/ui';
import axios from 'axios';
import * as timeago from 'timeago.js';
import { GlMarkdown, GlComment } from '~/components';
import { dateTimeMixin } from '~/utils/datetime';

export default {
  name: 'IncidentDetailsPage',
  components: {
    GlMarkdown,
    GlComment,
    GlLoadingIcon,
    GlIcon,
    GlBadge,
    GlAlert,
    GlTooltip,
  },
  mixins: [dateTimeMixin],
  data() {
    return {
      loading: true,
      incident: null,
      showAlert: false,
    };
  },
  computed: {
    id() {
      return this.$route.params.id;
    },
    updatedAt() {
      return this.incident.updated_at ? `Edited ${timeago.format(this.incident.updated_at)}` : '';
    },
    incidentVariant() {
      return this.incident.status === 'closed' ? 'success' : 'danger';
    },
  },
  mounted() {
    this.fetchIncidentDetails();
  },
  methods: {
    fetchIncidentDetails() {
      return axios
        .get(this.$route.params.link)
        .then(({ data }) => {
          this.incident = data;
          return data;
        })
        .catch(() => {
          this.showAlert = true;
        })
        .finally(() => {
          this.loading = false;
        });
    },
  },
};
</script>

<template>
  <div class="details gl-mx-3">
    <div class="gl-py-6 gl-inset-border-b-1-gray-200">
      <router-link
        :to="{ name: 'list' }"
        class="gl-link gl-text-decoration-none gl-display-inline-flex gl-align-items-center"
        data-qa-selector="incident-back-button"
      >
        <gl-icon
          name="arrow-left"
          title="Back"
          :size="14"
          class="gl-vertical-align-middle gl-mr-2"
        />
        Return to status overview
      </router-link>
    </div>
    <gl-loading-icon v-if="loading" size="lg" class="gl-mt-5" />
    <div v-else-if="incident" class="gl-px-5">
      <div class="gl-pb-3">
        <h1 class="gl-my-5" data-qa-selector="incident-title">{{ incident.title }}</h1>
        <div class="gl-mb-6">
          <gl-badge :variant="incidentVariant" pill data-qa-selector="incident-status">
            <span class="text-capitalize">{{ incident.status }}</span>
          </gl-badge>
        </div>
        <gl-markdown :text="incident.description" data-qa-selector="incident-details" />
        <div
          ref="updatedAtDateTime"
          class="gl-display-inline-block gl-mt-5 gl-font-sm gl-text-gray-700"
        >
          {{ updatedAt }}
        </div>
        <gl-tooltip :target="() => $refs.updatedAtDateTime">
          <div class="gl-display-block gl-align-items-center gl-font-sm">
            <div class="mb-1">{{ formatDateTime(incident.updated_at) }}</div>
            <div>{{ formatDateTimeZone(incident.updated_at) }}</div>
          </div>
        </gl-tooltip>
      </div>
      <div v-if="incident.comments.length">
        <h2 class="gl-mt-6 gl-mb-3">Updates</h2>
        <div class="gl-pt-5">
          <gl-comment
            v-for="(comment, index) in incident.comments"
            :key="index"
            :note="comment.note"
            :created-at="comment.created_at"
          />
        </div>
      </div>
    </div>
    <gl-alert
      v-else-if="showAlert"
      variant="danger"
      data-qa-selector="incident-detail-error"
      @dismiss="showAlert = false"
    >
      There was an error loading the incident's detail page. Please refresh the page or try again
      later.
    </gl-alert>
  </div>
</template>
